import { FlowObjects } from "../fsm/types";
import { Alignment } from "../../model/FontAttrsArray";

/**
 * Get current date in current locale as Pacific Time.
 */
export const getCurrentDate = () => {
  let currentDate = new Date().toLocaleString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  //  currentDate += " PDT";
  return currentDate;
};

/**
 * Random ID generator for all elements.
 */
export const guidGenerator = () => {
  const S4 = function () {
    return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  };
  return `${S4() + S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
};

export const alignmentToString = (align: Alignment) => {
  switch (align) {
    case Alignment.LEFT: {
      return "left";
    }
    case Alignment.RIGHT: {
      return "right";
    }
    case Alignment.CENTER: {
      return "center";
    }
  }
};

export const stringToAlignment = (align: "left" | "center" | "right") => {
  switch (align) {
    case "left": {
      return Alignment.LEFT;
    }
    case "right": {
      return Alignment.RIGHT;
    }
    case "center": {
      return Alignment.CENTER;
    }
  }
};

// copied from https://stackoverflow.com/a/16639126
// Assumption: object has originalImageAsset, croppedImageAsset in base64 string
export async function resizeLogic(
  object: FlowObjects,
  viewSize: { width: number; height: number }
): Promise<FlowObjects> {
  return new Promise<FlowObjects>((resolve, reject) => {
    let image = new Image();
    if (object.originalImageAsset!.externalURL.__tag === "string")
      image.src = object.originalImageAsset!.externalURL.value;
    image.onload = () => {
      object.frame.width = image.width;
      object.frame.height = image.height;
      const MAX_H = viewSize.height - 20;
      const MAX_W = viewSize.width - 20;
      if (image.width > viewSize.width || image.height > viewSize.height) {
        if (image.width > image.height) {
          const aspectRatio = image.width / image.height;
          image.width = viewSize.width - 40;
          image.height = (viewSize.width - 40) / aspectRatio;
        } else {
          const aspectRatio = image.height / image.width;
          image.height = viewSize.height - 40;
          image.width = (viewSize.height - 40) / aspectRatio;
        }

        const scale_width = MAX_W / object.frame.width;
        const scale_height = MAX_H / object.frame.height;

        const scale = Math.min(scale_width, scale_height);

        object.frame.width *= scale;
        object.frame.height *= scale;
      }

      object.frame.x = (MAX_W - object.frame.width) / 2;
      object.frame.y = (MAX_H - object.frame.height) / 2;
      resolve(object);
    };
  });
}

// copied from https://stackoverflow.com/a/16972036
export function dataURLtoFile(dataurl: string, filename: string) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)![1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * Extracts `screen` param from current `window.location` path
 *
 * Returns value as an int.
 *
 * Supports both old query string format (?screen=1) and new path format (/screen/1)
 * If the screen param does not exist, redirects to /screen/1
 *
 * Only manipulates URLs on flow editor pages (/flow/{id}/edit)
 */
export const indexFromUrl = () => {
  const url = new URL(window.location.toString());

  // Only manipulate URLs on flow editor pages
  const isFlowEditorPage = /\/flow\/[^/]+\/edit/.test(url.pathname);
  if (!isFlowEditorPage) {
    return 0;
  }

  // Try new path-based format first: /flow/{id}/edit/screen/1
  const pathMatch = url.pathname.match(/\/screen\/(\d+)/);
  if (pathMatch) {
    return parseInt(pathMatch[1]) - 1;
  }

  // Fall back to old query string format for backwards compatibility
  const screenNum = url.searchParams.get("screen");
  if (screenNum !== null) {
    // Redirect to new path format
    const basePath = url.pathname.replace(/\/edit.*/, '/edit');
    window.history.replaceState({}, "screen", `${basePath}/screen/${screenNum}`);
    return parseInt(screenNum) - 1;
  }

  // No screen specified - redirect to screen 1
  const basePath = url.pathname.replace(/\/edit.*/, '/edit');
  window.history.pushState({}, "screen", `${basePath}/screen/1`);
  return 0;
};

export default guidGenerator;

/**
 * Shifts an object within a list.
 * @param arr a list
 * @param old_index an index into `arr`
 * @param new_index new index to move object at `old_index` to
 */
export function move<T>(arr: T[], old_index: number, new_index: number): T[] {
  while (old_index < 0) {
    old_index += arr.length;
  }
  while (new_index < 0) {
    new_index += arr.length;
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  return arr;
}

/**
 * Free users can only have 10 screens or less in a flow
 * @param isPaidUser {@link FlowWindow.paidUser}
 * @param numScreens length of {@link Flow.screens}
 */
export const allowAdd = (isPaidUser: 0 | 1, numScreens: number) => {
  if (isPaidUser === 1) {
    return true;
  }
  return numScreens < 5;
};
